import { useNavigate } from 'react-router';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { AuthForm } from './AuthForm';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Where to send the user after a successful login. Defaults to '/post-auth',
   * which role-routes to the helper/client dashboard. Pass an explicit path to
   * continue an in-page flow instead (e.g. BecomeHelper → '/onboarding'). */
  redirectTo?: string;
}

/**
 * Inline sign-in/sign-up entry point — a Dialog wrapper around the one
 * canonical AuthForm (see AuthForm.tsx for why there's only one now). On
 * success, navigates to `redirectTo` if given, otherwise to '/post-auth' so
 * the user lands on their role dashboard rather than staying on whatever
 * page the modal was opened from.
 */
export function AuthModal({ isOpen, onClose, redirectTo }: AuthModalProps) {
  const modalRef = useFocusTrap(isOpen, onClose, { returnFocusOnClose: true });
  const navigate = useNavigate();

  const handleSuccess = () => {
    onClose();
    navigate(redirectTo ?? '/post-auth');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        ref={modalRef}
        className="sm:max-w-md max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={onClose}
        aria-labelledby="auth-modal-title"
        aria-describedby="auth-modal-description"
      >
        <DialogHeader>
          <DialogTitle id="auth-modal-title" className="sr-only">
            Welcome to Kanfy
          </DialogTitle>
          <div id="auth-modal-description" className="sr-only">
            Authentication modal. Press Escape to close.
          </div>
        </DialogHeader>

        {isOpen && <AuthForm onSuccess={handleSuccess} />}
      </DialogContent>
    </Dialog>
  );
}
